import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import api from "../../services/api";
import { formatPlanning } from "utils/planning";
import { useTranslation } from "react-i18next";
import { libelleMatiere } from "utils/valeurs";


function ResultSection({ title, icon, color, bg, results, onNavigate }) {
  if (results.length === 0) return null;
  return (
    <MDBox mb={3}>
      <MDBox display="flex" alignItems="center" gap={1} mb={1.5}>
        <MDBox sx={{
          width: 28, height: 28, borderRadius: "8px",
          background: color, display: "flex", alignItems: "center", justifyContent: "center",
        }}>
          <Icon sx={{ color: "#fff", fontSize: 14 }}>{icon}</Icon>
        </MDBox>
        <MDTypography variant="h6" fontWeight="bold" sx={{ color: "#344767", fontSize: 15 }}>
          {title} ({results.length})
        </MDTypography>
      </MDBox>
      {results.map((item) => (
        <MDBox key={item.id}
          onClick={() => onNavigate(item)}
          display="flex" alignItems="center" gap={2}
          py={1.5} px={2} mb={1}
          sx={{
            background: bg, borderRadius: "10px",
            border: `1px solid ${color}22`,
            cursor: "pointer", transition: "all 0.2s",
            "&:hover": { background: color, "& *": { color: "#fff !important" } },
          }}
        >
          <MDBox sx={{
            width: 36, height: 36, borderRadius: "50%",
            background: color, color: "#fff",
            display: "flex", alignItems: "center", justifyContent: "center",
            fontWeight: 700, fontSize: 14, flexShrink: 0,
          }}>
            {item.photo ? (
              <img src={`${item.photo}`}
                alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} />
            ) : (
              (item.prenom?.[0] || item.nom?.[0] || "?").toUpperCase()
            )}
          </MDBox>
          <MDBox flex={1}>
            <MDTypography variant="button" fontWeight="bold" sx={{ color: "#344767" }}>
              {item.label}
            </MDTypography>
            {item.sublabel && (
              <MDTypography variant="caption" color="text" display="block">
                {item.sublabel}
              </MDTypography>
            )}
          </MDBox>
          <Icon sx={{ color, fontSize: 18 }}>arrow_forward</Icon>
        </MDBox>
      ))}
    </MDBox>
  );
}

function Recherche() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState({
    eleves: [], enseignants: [], cours: [], livres: [],
  });
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get("search") || "";
    if (q) {
      setQuery(q);
      doSearch(q);
    }
  }, [location.search]);

  const doSearch = async (q) => {
    if (!q.trim()) return;
    setLoading(true);
    setSearched(true);
    try {
      const [elevesRes, enseignantsRes, coursRes, livresRes] = await Promise.all([
        api.get("/eleves"),
        api.get("/enseignants"),
        api.get("/cours?tous=true"),
        api.get("/livres"),
      ]);

      const term = q.toLowerCase();

      const eleves = elevesRes.data
        .filter((e) =>
          `${e.prenom} ${e.nom}`.toLowerCase().includes(term) ||
          e.email?.toLowerCase().includes(term) ||
          e.ville?.toLowerCase().includes(term) ||
          e.telephone?.toLowerCase().includes(term)
        )
        .map((e) => ({
          ...e,
          label: `${e.prenom} ${e.nom}`,
          sublabel: [e.ville, e.telephone, e.email].filter(Boolean).join(" · "),
        }));

      const enseignants = enseignantsRes.data
        .filter((e) =>
          `${e.prenom} ${e.nom}`.toLowerCase().includes(term) ||
          e.email?.toLowerCase().includes(term) ||
          e.ville?.toLowerCase().includes(term)
        )
        .map((e) => ({
          ...e,
          label: `${e.prenom} ${e.nom}`,
          sublabel: [e.ville, e.email].filter(Boolean).join(" · "),
        }));

      const cours = coursRes.data
        .filter((c) =>
          `${c.eleve?.prenom} ${c.eleve?.nom}`.toLowerCase().includes(term) ||
          `${c.enseignant?.prenom} ${c.enseignant?.nom}`.toLowerCase().includes(term) ||
          c.lieu?.toLowerCase().includes(term) ||
          libelleMatiere(c.matiere)?.toLowerCase().includes(term)
        )
        .map((c) => ({
          ...c,
          label: `${libelleMatiere(c.matiere)} — ${c.eleve?.prenom} ${c.eleve?.nom}`,
          sublabel: [
            c.enseignant ? t("recherche.prof", { nom: `${c.enseignant.prenom} ${c.enseignant.nom}` }) : null,
            c.lieu,
            formatPlanning(c) || null,
          ].filter(Boolean).join(" · "),
        }));

      const livres = livresRes.data
        .filter((l) =>
          l.nom.toLowerCase().includes(term) ||
          libelleMatiere(l.matiere)?.toLowerCase().includes(term)
        )
        .map((l) => ({
          ...l,
          label: l.nom,
          sublabel: libelleMatiere(l.matiere),
        }));

      setResults({ eleves, enseignants, cours, livres });
    } catch (error) {
      console.error("Erreur recherche:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalResults = results.eleves.length + results.enseignants.length +
    results.cours.length + results.livres.length;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} md={8}>
            <Card sx={{ borderRadius: "16px", boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>

              {/* Header */}
              <MDBox mx={2} mt={-3} py={3} px={3} sx={{
                background: "linear-gradient(135deg, #344767 0%, #485775 100%)",
                borderRadius: "12px",
                boxShadow: "0 4px 20px rgba(52,71,103,0.4)",
              }}>
                <MDTypography variant="h5" color="white" fontWeight="bold">
                  {t("recherche.titre")}
                </MDTypography>
                {searched && (
                  <MDTypography variant="caption" color="white" sx={{ opacity: 0.85 }}>
                    {loading ? t("recherche.enCours") : t("recherche.resultats", { count: totalResults, query })}
                  </MDTypography>
                )}
              </MDBox>

              <MDBox p={3}>
                {/* Champ de recherche */}
                <MDBox mb={3} display="flex" gap={1}>
                  <input
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && doSearch(query)}
                    placeholder={t("recherche.placeholder")}
                    autoFocus
                    style={{
                      flex: 1, height: 44, padding: "0 16px",
                      borderRadius: "10px", border: "1px solid #d2d6da",
                      fontSize: 14, outline: "none", fontFamily: "inherit",
                    }}
                  />
                  <MDBox
                    onClick={() => doSearch(query)}
                    sx={{
                      height: 44, px: 3, borderRadius: "10px",
                      background: "linear-gradient(135deg, #344767, #485775)",
                      color: "#fff", display: "flex", alignItems: "center",
                      justifyContent: "center", cursor: "pointer",
                      fontSize: 13, fontWeight: 700, gap: 1,
                      "&:hover": { background: "#263238" },
                    }}>
                    <Icon sx={{ fontSize: 18 }}>search</Icon>
                    {t("commun.rechercher")}
                  </MDBox>
                </MDBox>

                {/* Résultats */}
                {loading ? (
                  <MDBox display="flex" justifyContent="center" py={4}>
                    <MDTypography variant="body2" color="text">{t("recherche.enCours")}</MDTypography>
                  </MDBox>
                ) : searched && totalResults === 0 ? (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>search_off</Icon>
                    <MDTypography variant="h6" color="text" fontWeight="medium">
                      {t("cours.form.aucunResultat", { query })}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      {t("recherche.autresMots")}
                    </MDTypography>
                  </MDBox>
                ) : searched ? (
                  <>
                    <ResultSection
                      title={t("champs.eleves")}
                      icon="school"
                      color="#1565c0"
                      bg="#f0f7ff"
                      results={results.eleves}
                      onNavigate={(item) => navigate(`/eleves/${item.id}`)}
                    />
                    <ResultSection
                      title={t("champs.enseignants")}
                      icon="person"
                      color="#6a1b9a"
                      bg="#fdf8ff"
                      results={results.enseignants}
                      onNavigate={(item) => navigate(`/enseignants/${item.id}`)}
                    />
                    <ResultSection
                      title={t("champs.cours")}
                      icon="menu_book"
                      color="#2e7d32"
                      bg="#f8fff8"
                      results={results.cours}
                      onNavigate={(item) => navigate(`/cours/${item.id}`)}
                    />
                    <ResultSection
                      title={t("champs.livres")}
                      icon="library_books"
                      color="#e65100"
                      bg="#fff8f5"
                      results={results.livres}
                      onNavigate={(item) => navigate(`/livres`)}
                    />
                  </>
                ) : (
                  <MDBox display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
                    <Icon sx={{ fontSize: 60, color: "#e0e0e0" }}>search</Icon>
                    <MDTypography variant="body2" color="text">
                      {t("recherche.aide")}
                    </MDTypography>
                  </MDBox>
                )}
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Recherche;